'use client'
import Image from 'next/image'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useState } from 'react'
import copy from '../_content/site-copy.json'
import type { Artifact } from '../_lib/data'
import { MuseumNav } from './MuseumNav'
type Preview = Pick<
  Artifact,
  'id' | 'title' | 'year' | 'era' | 'kind' | 'image'
>
export function IndexGrid({ items }: { items: Preview[] }) {
  const params = useSearchParams()
  const eras = new Set(params.getAll('era'))
  const kinds = new Set(params.getAll('kind'))
  const [open, setOpen] = useState(Boolean(eras.size || kinds.size))
  const [active, setActive] = useState<string | null>(null)
  const shown = items.filter(
    (item) =>
      (!eras.size || eras.has(item.era)) &&
      (!kinds.size || kinds.has(item.kind)),
  )
  const hovered = shown.find((item) => item.id === active)
  const filter = (key: string, value: string) => {
    const next = new URLSearchParams(params.toString())
    const selected = next.getAll(key)
    next.delete(key)
    if (value) {
      const values = selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value]
      values.forEach((item) => next.append(key, item))
    }
    window.history.pushState(
      null,
      '',
      `/museum/index${next.size ? '?' + next.toString() : ''}`,
    )
    setActive(null)
  }
  return (
    <>
      <h1 className="sr-only">{copy.index.title}</h1>
      <MuseumNav>
        <div className="museum-index-filter-control">
          <button
            className="museum-filter-toggle"
            type="button"
            aria-expanded={open}
            aria-controls="museum-index-filters"
            onClick={() => setOpen(!open)}
          >
            <span className="museum-filter-square" aria-hidden="true" />
            {open ? copy.index.filtersHide : copy.index.filtersShow}
          </button>
          <div
            className="museum-filter-reveal"
            data-open={open}
            inert={!open}
            aria-hidden={!open}
          >
            <div>
              <div
                className="museum-filters"
                id="museum-index-filters"
                data-lenis-prevent
              >
                <fieldset className="museum-room-filters">
                  <legend className="sr-only">{copy.index.allEras}</legend>
                  <div className="museum-filter-column">
                    <FilterChoice
                      label={copy.index.allEras}
                      checked={!eras.size}
                      onChange={() => filter('era', '')}
                    />
                    {copy.exhibits.slice(0, 4).map((room) => (
                      <FilterChoice
                        key={room.id}
                        label={room.title}
                        checked={eras.has(room.id)}
                        onChange={() => filter('era', room.id)}
                      />
                    ))}
                  </div>
                  <div className="museum-filter-column">
                    {copy.exhibits.slice(4).map((room) => (
                      <FilterChoice
                        key={room.id}
                        label={room.title}
                        checked={eras.has(room.id)}
                        onChange={() => filter('era', room.id)}
                      />
                    ))}
                  </div>
                </fieldset>
                <fieldset className="museum-kind-filters museum-filter-column">
                  <legend className="sr-only">{copy.index.allKinds}</legend>
                  <FilterChoice
                    label={copy.index.allKinds}
                    checked={!kinds.size}
                    onChange={() => filter('kind', '')}
                  />
                  {Object.entries(copy.index.kinds).map(([value, label]) => (
                    <FilterChoice
                      key={value}
                      label={label}
                      checked={kinds.has(value)}
                      onChange={() => filter('kind', value)}
                    />
                  ))}
                </fieldset>
              </div>
            </div>
          </div>
        </div>
      </MuseumNav>
      <div className="museum-hover-heading" aria-live="polite">
        <h2 key={hovered?.id} className={hovered ? 'museum-hover-title' : ''}>
          {hovered?.title}
        </h2>
      </div>
      <div
        className="museum-index-grid"
        data-hover={hovered ? 'true' : undefined}
        onMouseLeave={() => setActive(null)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget))
            setActive(null)
        }}
      >
        {shown.map((item, index) => (
          <Link
            key={item.id}
            href={`/museum/items/${item.id}`}
            data-active={item.id === active}
            onPointerEnter={(event) => {
              if (event.pointerType === 'mouse') setActive(item.id)
            }}
            onFocus={() => setActive(item.id)}
            className="museum-index-item"
          >
            <Image
              src={item.image.src}
              alt={item.title}
              width={item.image.width}
              height={item.image.height}
              sizes="(max-width: 640px) 20vw, 80px"
              priority={index < 16}
            />
            <span>
              {item.title}
              <small>{item.year}</small>
            </span>
          </Link>
        ))}
      </div>
      {shown.length === 0 && <p className="museum-empty">{copy.index.empty}</p>}
    </>
  )
}

function FilterChoice({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: () => void
}) {
  return (
    <label className="museum-filter-choice">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span>{label}</span>
    </label>
  )
}
